import { useEffect, useRef } from "react";

import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { CircleExclamationFilledIcon, LoadingDotsIcon, RefreshIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectShowVersionMismatchWarning } from "store/selectors/view";
import { GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { useConnectionAlertStatusObserver } from "utils/hooks/use-connection-alert-status-observer";
import { clearCacheAndReload } from "utils/urls";

export const GlobalBanners = () => {
  const connectionAlertStatus = useConnectionAlertStatusObserver();
  const hasTrackedConnectionBannerRef = useRef<boolean>(false);
  const hasTrackedVersionMismatchBannerRef = useRef<boolean>(false);
  const showVersionMismatchWarning = useAppSelector(selectShowVersionMismatchWarning);

  useEffect(() => {
    if (connectionAlertStatus && !hasTrackedConnectionBannerRef.current) {
      hasTrackedConnectionBannerRef.current = true;
      trackEvent("Connection alert banner displayed", GENERAL_EVENT_CATEGORY);
    }
    if (!connectionAlertStatus) {
      hasTrackedConnectionBannerRef.current = false;
    }
  }, [connectionAlertStatus]);

  useEffect(() => {
    if (
      showVersionMismatchWarning &&
      !connectionAlertStatus &&
      !hasTrackedVersionMismatchBannerRef.current
    ) {
      hasTrackedVersionMismatchBannerRef.current = true;
      trackEvent("Outdated version banner displayed", GENERAL_EVENT_CATEGORY);
    }
  }, [showVersionMismatchWarning, connectionAlertStatus]);

  if (connectionAlertStatus) {
    const isFailed = connectionAlertStatus === "failed";
    return (
      <Banner
        buttonChildren={
          isFailed ? (
            <SquareButton
              eventName="Connection banner - Reload Forum"
              kind="tertiary"
              size="s"
              onClick={clearCacheAndReload}
            >
              <RefreshIcon />
              {t("websocket.connection_banner.reload_button")}
            </SquareButton>
          ) : (
            <div className="flex items-center gap-2 px-2">
              <LoadingDotsIcon size="s" />
              <span className="hidden text-sm font-medium text-red-shade-20 md:block">
                {connectionAlertStatus === "reconnecting"
                  ? t("websocket.connection_banner.reconnecting_short")
                  : t("websocket.connection_banner.retrying")}
              </span>
              <span className="block text-sm font-medium text-red-shade-20 md:hidden">
                {connectionAlertStatus === "reconnecting"
                  ? t("websocket.connection_banner.reconnecting_short")
                  : t("websocket.connection_banner.retrying_short")}
              </span>
            </div>
          )
        }
        className="rounded-none border-0 border-b border-red-tint-40 pl-4"
        eventName={isFailed ? "Connection banner - failed" : "Connection banner - reconnecting"}
        Icon={CircleExclamationFilledIcon}
        kind="error"
      >
        <>
          <span className="hidden md:inline">
            {isFailed
              ? t("websocket.connection_banner.failed_message")
              : t("websocket.connection_banner.message")}
          </span>
          <span className="inline md:hidden">
            {isFailed
              ? t("websocket.connection_banner.failed_message_short")
              : t("websocket.connection_banner.message_short")}
          </span>
        </>
      </Banner>
    );
  }

  if (showVersionMismatchWarning) {
    return (
      <Banner
        actionButtonText={t("warning.version_mismatch.reload_button")}
        className="rounded-none border-0 border-b border-blue-tint-40 pl-4"
        closeEventName="Outdated version banner - Dismiss"
        eventName="Outdated version banner - Refresh now"
        Icon={CircleExclamationFilledIcon}
        kind="info"
        onAction={clearCacheAndReload}
      >
        {t("warning.version_mismatch.message")}
      </Banner>
    );
  }

  return null;
};
